import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchOpportunityList,
  searchOpportunities,
  type OpportunityListItem,
} from '../lib/opportunities';
import {
  OPPORTUNITY_STAGE_COLORS,
  OPPORTUNITY_TYPE_COLORS,
  OPPORTUNITY_STAGE_GROUPS,
  type OpportunityStageGroup,
} from '../lib/opportunity-constants';
import { formatCurrency, formatRelativeTime } from '../lib/format';

export function OpportunitiesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<OpportunityListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OpportunityListItem[] | null>(null);
  const [stageGroup, setStageGroup] = useState<OpportunityStageGroup>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOpportunityList()
      .then((list) => {
        if (!cancelled) {
          setItems(list);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Kon opportunities niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      searchOpportunities(trimmed, controller.signal)
        .then((results) => {
          setSearchResults(results);
          setSearching(false);
        })
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setSearching(false);
          setSearchResults([]);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const isSearching = query.trim().length >= 2;

  const stageCounts = useMemo(() => {
    const counts: Record<OpportunityStageGroup, number> = { all: items.length, open: 0, won: 0, lost: 0 };
    for (const it of items) {
      if (OPPORTUNITY_STAGE_GROUPS.open.stages.includes(it.stage)) counts.open += 1;
      if (OPPORTUNITY_STAGE_GROUPS.won.stages.includes(it.stage)) counts.won += 1;
      if (OPPORTUNITY_STAGE_GROUPS.lost.stages.includes(it.stage)) counts.lost += 1;
    }
    return counts;
  }, [items]);

  const displayItems = useMemo(() => {
    if (isSearching) return searchResults ?? [];
    if (stageGroup === 'all') return items;
    const allowed = OPPORTUNITY_STAGE_GROUPS[stageGroup].stages;
    return items.filter((it) => allowed.includes(it.stage));
  }, [isSearching, searchResults, stageGroup, items]);

  const groupOrder: OpportunityStageGroup[] = ['all', 'open', 'won', 'lost'];

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Opportunities</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {isSearching
                  ? 'Zoekt in alle Cinco-opportunities'
                  : 'Cinco-opportunities van de laatste 90 dagen'}
              </p>
            </div>
            <div className="relative flex-1 max-w-sm">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek opp-nr, klant, partner, email, telefoon..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {!isSearching && (
            <div className="flex items-center gap-2 mt-3">
              {groupOrder.map((g) => {
                const label = OPPORTUNITY_STAGE_GROUPS[g].label;
                const count = stageCounts[g];
                const active = stageGroup === g;
                return (
                  <button
                    key={g}
                    onClick={() => setStageGroup(g)}
                    className={clsx(
                      'text-xs px-3 py-1 rounded-full font-medium transition-colors',
                      active
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    )}
                  >
                    {label} <span className={clsx('ml-1', active ? 'text-indigo-100' : 'text-slate-400')}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          ) : !isSearching && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-sm">Geen Cinco-opportunities in de laatste 90 dagen</p>
            </div>
          ) : !isSearching && displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-sm">Geen opportunities in deze categorie</p>
            </div>
          ) : isSearching && !searching && displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">Geen opportunities gevonden voor "{query}"</p>
              <button
                onClick={() => setQuery('')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Zoekopdracht wissen
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-6 py-2">Opp.nr</th>
                  <th className="text-left font-medium px-4 py-2">Klant</th>
                  <th className="text-left font-medium px-4 py-2">Aanvraag</th>
                  <th className="text-left font-medium px-4 py-2">Partner</th>
                  <th className="text-left font-medium px-4 py-2">Stage</th>
                  <th className="text-left font-medium px-4 py-2">Type</th>
                  <th className="text-right font-medium px-4 py-2">Bedrag</th>
                  <th className="text-left font-medium px-4 py-2">Leeftijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayItems.map((row) => {
                  const aanvraag = row.primaryProduct
                    ? `${row.primaryProduct}${row.primaryQuantity > 1 ? ` ×${row.primaryQuantity}` : ''}${row.lineItemCount > 1 ? ` +${row.lineItemCount - 1}` : ''}`
                    : '—';
                  return (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/opportunities/${row.id}`)}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-3 font-medium text-slate-900">
                        {row.opportunityNumber || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="truncate max-w-[180px]">{row.customerName || row.name || '—'}</div>
                        {row.customerCity && (
                          <div className="text-xs text-slate-500 truncate max-w-[180px]">{row.customerCity}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="truncate max-w-[200px]">{aanvraag}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="truncate max-w-[160px]">{row.partnerName || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={clsx(
                            'text-[10px] px-1.5 py-0.5 rounded font-medium',
                            OPPORTUNITY_STAGE_COLORS[row.stage] || 'bg-slate-100 text-slate-600'
                          )}
                        >
                          {row.stage || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {row.type && (
                          <span
                            className={clsx(
                              'text-[10px] px-1.5 py-0.5 rounded font-medium',
                              OPPORTUNITY_TYPE_COLORS[row.type] || 'bg-slate-100 text-slate-600'
                            )}
                          >
                            {row.type}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-900">
                        {row.totalInclBtw > 0 ? formatCurrency(row.totalInclBtw) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {formatRelativeTime(row.createdDate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
